import { LogOut, Menu, Moon, Plus, Sun } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { Suchfeld } from '../suche/Suchfeld';
import { Button } from '../ui/elements';
import type { FarbschemaSteuerung } from './farbschema';
import type { NavHauptaktion } from './navigation';
import type { Route } from './router';

/**
 * Die klebende Kopfzeile: Hamburger, Brotkrumen, Farbschema, Abmelden, «Neu».
 *
 * **Der frühere Knopf «Passwort» entfällt seit `W-profilseite`
 * (2026-08-31).** Der Passwortwechsel ist in die Profilseite umgezogen
 * (`/profil`, `profil/ProfilePage.tsx`), erreichbar über die Sitzungsecke
 * unten links in der Menüspalte (`Sidebar.tsx`, `Anwenderzeile`) — die
 * Kopfzeile trägt seither einen Knopf weniger.
 *
 * **Warum das Kleben hier gefahrlos ist — und die Vorgeschichte, weshalb der
 * Satz dasteht.** Unsere Kopfzeile klebte bewusst NICHT: Eine klebende
 * Kopfzeile schiebt sich bei einem Klick auf ein Element am oberen Rand
 * darüber, und Playwright bricht dann mit «element intercepts pointer events»
 * ab — an einer Stelle, die mit der Kopfzeile nichts zu tun hat. Der Rahmen
 * löst das ab `lg` anders als über `position: sticky` allein: Die Kopfzeile ist
 * ein Geschwister der Arbeitsfläche in einer Spalte fester Höhe, und
 * **gerollt wird die Arbeitsfläche selbst** (`Shell.tsx`). Damit liegt die
 * Kopfzeile nie ÜBER Inhalt, sondern immer daneben — ein Element kann gar
 * nicht unter sie geraten. `sticky top-0` bleibt trotzdem stehen: Unterhalb von
 * `lg` läuft die Seite im gewöhnlichen Fluss, und dort ist es die einzige
 * Wirkung.
 *
 * **Die Brotkrumen sind Text und keine Verweise.** Ein Brotkrumenverweis
 * «Bankkonten» stünde auf `/bankkonten/<id>` neben dem Menüverweis
 * «Bankkonten», und `bank-accounts.spec.ts` sowie `zahldienste.spec.ts` fänden
 * zwei Treffer — strenge Eindeutigkeit, Fehlschlag. Der Weg zurück steht in
 * der Menüspalte, und die ist immer sichtbar.
 */
export function Kopfzeile(props: {
  readonly brotkrumen: readonly string[];
  readonly eingeklappt: boolean;
  readonly onEinklappen: () => void;
  readonly farbschema: FarbschemaSteuerung;
  readonly hauptaktion: NavHauptaktion | undefined;
  readonly navigate: (route: Route) => void;
  readonly onSignOut: () => void;
}): ReactNode {
  return (
    <header className="sticky top-0 z-[5] flex shrink-0 flex-wrap items-center gap-x-[14px] gap-y-2 border-b border-border bg-card px-4 py-[13px] lg:flex-nowrap lg:px-7">
      <button
        type="button"
        aria-label={
          props.eingeklappt ? 'Menüspalte ausklappen' : 'Menüspalte einklappen'
        }
        onClick={props.onEinklappen}
        // Nur ab `lg` — unterhalb davon ist die Menüspalte ein Block über dem
        // Inhalt und hat nichts einzuklappen. Ein Knopf, der dort nichts täte,
        // wäre eine Zusage ohne Wirkung.
        //
        // `size-[34px]` und nicht mehr `size-8` (32 px) — Design-Annäherung
        // Spec 5.5 / Task `V-restseiten`: „Header-Buttons … 34px". Quadratisch
        // bleibt er, damit ein Sinnbild-Knopf ein Sinnbild-Knopf bleibt.
        className="hidden size-[34px] shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground outline-none transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card lg:flex"
      >
        <Menu aria-hidden="true" strokeWidth={1.7} className="size-[18px]" />
      </button>

      <nav aria-label="Brotkrumen" className="min-w-0">
        <ol className="flex min-w-0 items-center gap-1.5 text-[13px]">
          {props.brotkrumen.map((segment, index) => (
            <Fragment key={segment}>
              {index === 0 ? null : (
                <li aria-hidden="true" className="shrink-0 text-text3">
                  /
                </li>
              )}
              <li
                className={
                  index === props.brotkrumen.length - 1
                    ? 'truncate font-medium text-foreground'
                    : 'shrink-0 text-text3'
                }
              >
                {segment}
              </li>
            </Fragment>
          ))}
        </ol>
      </nav>

      {/*
        Das Suchfeld sitzt an dem Platz, den das Uebergabepaket der
        Design-Annaeherung dafuer vorsieht (Spec 9.1):
        [Hamburger] [Brotkrumen] ——— [Suchfeld] [Farbschema] [Neu].

        Es ist das ERSTE Element der rechten Gruppe und traegt damit deren
        `ml-auto` mit: Der Zwischenraum entsteht vor dem Feld und nicht zwischen
        Feld und Knoepfen. Ein eigener Block daneben brauchte ein zweites
        `ml-auto`, und zwei davon in einer Flex-Zeile teilen den Zwischenraum
        auf statt ihn zu setzen.
      */}
      {/*
        `flex-wrap` statt `shrink-0`: Mit Text auf den Knoepfen ist die Gruppe
        rund 400 px breit — als unschrumpfbarer Block schob sie auf einem
        375-px-Schirm JEDE Seite in die Waagerechte (gemessen 2026-08-26 durch
        die Mobilprobe der Suchseite; der Uebeltaeter war die Kopfzeile, nicht
        die Seite). Umbruch behaelt alle Aufschriften; die Kopfzeile wird auf
        dem Telefon zweizeilig, was sie unterhalb `lg` ohnehin schon ist.
      */}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
        <Suchfeld navigate={props.navigate} />

        {/*
          Der Umschalter nennt das ZIEL und nicht den Zustand.

          «Helle Darstellung» heisst: ein Klick, und es wird hell. Die
          Gegenprobe wäre eine Anzeige des Zustands — «Dunkel», während es
          dunkel ist —, und die ist bei einem Knopf nicht lesbar: Ein Knopf mit
          der Aufschrift «Dunkel» in einer dunklen Oberfläche lässt genau die
          Frage offen, die er beantworten soll. Vorgelesen ist es derselbe
          Unterschied: «Schaltfläche Helle Darstellung» ist eine Handlung,
          «Schaltfläche Dunkel» eine Feststellung. Das Übergabepaket zeigt den
          Zustand; wir übernehmen seine Gestalt und behalten unsere Aufschrift.

          Und deshalb steht hier Text und nicht nur ein Sinnbild. Eine Sonne
          oder ein Mond müsste man deuten — in welche Richtung, weiss auch
          sehend niemand zuverlässig —, und ein Vorleseprogramm liest sie gar
          nicht. Das Sinnbild steht NEBEN dem Text und trägt `aria-hidden`.

          UND WARUM DIE KOPFZEILE KEIN BENUTZERMENÜ BEKOMMT.

          Das naheliegende Aufräumen wäre ein `dropdown-menu`, in das Name und
          «Abmelden» wandern (seit `W-profilseite` bräuchte «Passwort» keinen
          Platz mehr darin — der Knopf ist entfallen, die Maske steht jetzt
          unter `/profil`). Es geht trotzdem nicht:
          `e2e/first-login.spec.ts:70` verlangt `getByRole('button', { name:
          'Abmelden' })` SICHTBAR — das ist die Probe, mit der die
          Erstanmeldung als abgeschlossen gilt. Ein `DropdownMenuItem` hat die
          Rolle `menuitem` und nicht `button`, und vor dem Öffnen des Menüs
          gibt es ihn überhaupt nicht. Der Punkt fällt also in beiden
          Richtungen durch, nicht nur in einer.

          Dasselbe gilt für den Namen: `first-login.spec.ts:73` prüft
          `getByText('Erstanmeldung', { exact: true })`. Er steht jetzt im Fuss
          der Menüspalte (`Sidebar.tsx`) und ist dort ohne Klick lesbar.
        */}
        {/*
          `size="header"` (34 px) auf beiden Knöpfen dieser Zeile (seit
          `W-profilseite` zwei statt drei — «Passwort» ist entfallen) —
          Design-Annäherung Spec 5.5 / Task `V-restseiten`: „Header-Buttons …
          34px". Der Button-Baustein trägt die Begründung, warum das NICHT für
          jeden Knopf der Anwendung gilt, sondern nur für diese Kopfzeile
          (`ui/elements.tsx`).
        */}
        <Button variant="secondary" size="header" onClick={props.farbschema.umschalten}>
          {props.farbschema.schema === 'dunkel' ? (
            <Sun aria-hidden="true" className="size-4" />
          ) : (
            <Moon aria-hidden="true" className="size-4" />
          )}
          {props.farbschema.schema === 'dunkel' ? 'Helle Darstellung' : 'Dunkle Darstellung'}
        </Button>

        <Button variant="secondary" size="header" onClick={props.onSignOut}>
          <LogOut aria-hidden="true" className="size-4" />
          Abmelden
        </Button>

        {/*
          **Der Knopf heisst «Neu» und nennt sein Ziel im `title`.** Hiesse er
          «Beleg anlegen», stünden auf der Belegliste zwei Elemente mit
          demselben zugänglichen Namen — der zweite ist der Knopf in der Tafel
          «Belege», den `documents-create.spec.ts` greift —, und in Playwrights
          strenger Eindeutigkeit sind zwei Treffer ein Fehlschlag. Der `title`
          beschreibt den Knopf, ohne seinen Namen zu ändern: Der zugängliche
          Name kommt aus dem Inhalt und erst mangels Inhalt aus dem `title`.

          **Die Haupthandlung bleibt zusätzlich in der Tafel**, wo sie heute
          steht. Ein Knopf, der nur oben rechts steht, ist weit weg von dem,
          worauf er wirkt; dieser hier ist ein ZWEITER Weg und niemals der
          einzige.
        */}
        {props.hauptaktion === undefined ? null : (
          // `size="action"` (36 px) — Spec 5.5: „Handlungsknopf … 36px", der
          // EINE Knopf, der hier gemeint ist.
          <Button
            size="action"
            title={props.hauptaktion.titel}
            onClick={() => {
              if (props.hauptaktion !== undefined) props.navigate(props.hauptaktion.ziel);
            }}
          >
            <Plus aria-hidden="true" className="size-4" />
            Neu
          </Button>
        )}
      </div>
    </header>
  );
}
